import { useState } from "react";
import { Button } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { District } from "@/sharedTypes";
import DataTable from "@/components/Table";
import { useUpdateDistrictMutation, useDeleteDistrictMutation } from "@/api/locationApi/district";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import DistrictFormDialog from "./DistrictFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import { useTranslation } from "react-i18next";

interface DistrictListTableProps {
  data: District[];
  pagination: any;
  onPageChange: (page: number) => void;
}

export default function DistrictListTable({ data, pagination, onPageChange }: DistrictListTableProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState<District | undefined>();

  const [updateDistrict] = useUpdateDistrictMutation();
  const [deleteDistrict] = useDeleteDistrictMutation();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  } | null>(null);

  const handleEdit = (district: District) => {
    setSelectedDistrict(district);
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (data: District) => {
    if (!selectedDistrict) return;
    try {
      await updateDistrict({ ...data, id: selectedDistrict.id } as District).unwrap();
      toast.success(t("temporalActivity:district.updatedSuccess"));
      setIsEditModalOpen(false);
      setSelectedDistrict(undefined);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDelete = (district: District) => {
    setConfirmDialog({
      open: true,
      title: t("temporalActivity:district.confirmDeleteTitle"),
      description: t("temporalActivity:district.confirmDeleteBody"),
      onConfirm: async () => {
        try {
          await deleteDistrict(district.id).unwrap();
          toast.success(t("temporalActivity:district.deletedSuccess"));
        } catch (err) {
          toast.error(getErrorMessage(err));
        }
        setConfirmDialog(null);
      },
    });
  };

  const columns = [
    { key: "id", label: t("temporalActivity:district.ref") },
    { key: "district_name", label: t("temporalActivity:district.name") },
    {
      key: "city",
      label: t("temporalActivity:district.city"),
      render: (row: District) => <span>{row.city?.city_name ?? "—"}</span>,
    },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("temporalActivity:actions"),
            render: (row: District) => (
              <div className="flex gap-2">
                <Button size="xs" color="light" onClick={() => handleEdit(row)}>
                  <HiPencil className="h-4 w-4 text-purple-600" />
                </Button>
                <Button size="xs" color="light" onClick={() => handleDelete(row)}>
                  <HiTrash className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable columns={columns} data={data} pagination={pagination} onPageChange={onPageChange} />

      <DistrictFormDialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        defaultValues={selectedDistrict}
        mode="edit"
      />

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.description}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      )}
    </>
  );
}
